import React, { useCallback } from 'react';
import { Button, CompositeTextInput, Content, PortalOverlay, List, Popup, Text } from '@leverege/ui-elements';
import I18N from '@leverege/i18n';
import { connect } from 'react-redux';
import { ValueCache } from '@leverege/value-cache';
import { Toolbar } from '@leverege/ui-plugin';
import { ItemDelegate } from '@leverege/molten-core';
import DeviceUtil from "./DeviceUtil.js";
import useDeviceSelector from "./useDeviceSelector.js";
import S from './DeviceSelector.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t,
  tIcon
} = I18N.ns('molten.imagine.DeviceSelector');
const EMPTY_OBJ = {};
const client = 'DeviceSelector';
function DeviceSelector(props) {
  const {
    groupDelegate,
    values,
    value,
    eventData
  } = props;
  const {
    relationship
  } = groupDelegate || EMPTY_OBJ;
  const {
    getContext,
    nameAttribute,
    opened,
    search,
    setOpened,
    setSearch,
    onChange
  } = useDeviceSelector(props);
  const renderTrigger = useCallback(({
    ref
  }) => {
    const item = ItemDelegate.getItem(value);
    const name = nameAttribute?.get(item);
    return /*#__PURE__*/_jsx(CompositeTextInput, {
      variant: "mobileSearch",
      domRef: ref,
      hint: name || t('searchHint', {
        blueprintName: relationship?.objectTypeName || 'Device'
      }),
      icon: tIcon('search'),
      onFocus: () => setOpened(true),
      onBlur: () => {
        setTimeout(() => {
          setOpened(false);
        }, 250);
      },
      changeOnReturn: false,
      alwaysFireOnReturn: false,
      onChange: ({
        value
      }) => setSearch(value),
      value: search,
      post: /*#__PURE__*/_jsx(Toolbar, {
        variant: "actionBar",
        prefer: "icon",
        context: getContext(),
        matchContext: {
          client,
          objectType: relationship?.objectType
        }
      })
    });
  }, [search, nameAttribute, value, relationship, getContext, setOpened, setSearch]);
  return /*#__PURE__*/_jsx(Popup, {
    setPopupMinWidth: true,
    trigger: renderTrigger,
    opened: opened && search?.length > 0,
    variant: "editor",
    children: values?.length > 0 ? values.map(item => {
      const name = nameAttribute.get(item);
      return /*#__PURE__*/_jsx(Popup.Item, {
        onClick: onChange,
        eventData: eventData,
        value: item.id,
        checkStyle: "radio",
        checked: item.id === value,
        children: name
      }, item.id);
    }) : /*#__PURE__*/_jsx(Popup.Item, {
      disabled: true,
      children: t('noResult', {
        blueprintName: relationship?.objectTypeNamePlural || 'Devices'
      })
    })
  });
}
function DeviceSelectorOverlay(props) {
  const {
    eventData,
    itemRenderer: ItemRenderer,
    NoResult,
    result,
    searchHeaderTitle,
    targetId,
    values
  } = props;
  const {
    groupDelegate,
    value
  } = props;
  const {
    relationship
  } = groupDelegate;
  const {
    getContext,
    nameAttribute,
    opened,
    search,
    setOpened,
    setSearch,
    inputRef,
    onChange
  } = useDeviceSelector(props);
  const item = value ? ItemDelegate.getItem(value) : null;
  const name = item ? nameAttribute.get(item) : null;
  const toolbar = /*#__PURE__*/_jsx(Toolbar, {
    variant: "actionBar",
    prefer: "icon",
    context: getContext(),
    matchContext: {
      client: opened ? `${client}Opened` : client,
      objectType: relationship?.objectType
    }
  });
  if (!opened) {
    return /*#__PURE__*/_jsx(CompositeTextInput, {
      domRef: inputRef,
      variant: "mobileSearch",
      hint: t('searchHint', {
        blueprintName: relationship?.objectTypeName || 'Device'
      }),
      icon: item ? tIcon('success') : undefined,
      imageOpts: {
        height: 25,
        width: 25
      },
      onFocus: () => setOpened(true),
      onChange: ({
        value
      }) => {
        setSearch(value);
      },
      changeOnReturn: false,
      alwaysFireOnReturn: false,
      value: search ?? name,
      post: toolbar
    });
  }
  const total = !search ? 0 : result?.total;
  const height = inputRef?.current?.parentNode?.offsetHeight;
  return /*#__PURE__*/_jsxs(React.Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      style: {
        height
      }
    }), /*#__PURE__*/_jsx(PortalOverlay, {
      show: true,
      position: "center",
      targetId: targetId,
      animation: "fade",
      children: /*#__PURE__*/_jsxs(Content, {
        className: S.overlayEditor,
        variant: "deviceSelector",
        children: [/*#__PURE__*/_jsx(Content.Header, {
          variant: "deviceSelectorHeader",
          layout: "flex:rowMCenter",
          children: /*#__PURE__*/_jsx(Button, {
            icon: tIcon('back'),
            variant: "pairClose|linkNoPad",
            onClick: () => {
              setOpened(false);
              setSearch(null);
            }
          })
        }), /*#__PURE__*/_jsxs(Content.Area, {
          variant: "deviceSelectorContent",
          className: S.overlayContent,
          children: [/*#__PURE__*/_jsx(CompositeTextInput, {
            domRef: inputRef,
            autoFocus: true,
            variant: "mobileSearch",
            hint: t('searchHint', {
              blueprintName: relationship?.objectTypeName || 'Device'
            }),
            onFocus: () => setOpened(true),
            onChange: ({
              value
            }) => {
              setSearch(value);
            },
            changeOnReturn: false,
            alwaysFireOnReturn: false,
            value: search ?? name,
            post: toolbar
          }), /*#__PURE__*/_jsx(Text, {
            variant: "deviceSelectorResults",
            children: t('resultWithCount', {
              count: total
            })
          }), (!values || values?.length === 0) && NoResult ? /*#__PURE__*/_jsx(NoResult, {
            search: search,
            onChange: onChange,
            eventData: eventData,
            setOpened: setOpened,
            setSearch: setSearch
          }) : /*#__PURE__*/_jsxs(List, {
            className: S.list,
            variant: "pairing",
            onClick: evt => {
              onChange({
                data: eventData,
                value: evt.data
              });
            },
            children: [/*#__PURE__*/_jsx(List.Header, {
              children: searchHeaderTitle || t('headerTitle')
            }), search && values?.map(item => {
              const name = nameAttribute.get(item);
              return ItemRenderer ? /*#__PURE__*/_jsx(ItemRenderer, {
                item: item,
                onChange: onChange,
                relationship: relationship,
                name: name,
                eventData: eventData
              }) : /*#__PURE__*/_jsx(List.Item, {
                className: S.listItem,
                item: item.id,
                children: /*#__PURE__*/_jsx(Text, {
                  icon: "fa fa-fw fa-search",
                  children: name
                })
              }, item.id);
            })]
          })]
        })]
      })
    })]
  });
}
function DeviceSelectorValidator(props) {
  const {
    groupDelegate,
    usage
  } = props;
  if (!groupDelegate) {
    return /*#__PURE__*/_jsx(CompositeTextInput, {
      variant: "mobileSearch",
      hint: t('invalidRelationship'),
      disabled: true
    });
  }
  if (usage === 'pairing') {
    return /*#__PURE__*/_jsx(DeviceSelectorOverlay, {
      ...props
    });
  }
  return /*#__PURE__*/_jsx(DeviceSelector, {
    ...props
  });
}
const actionCache = new ValueCache();
const EMPTY_ARRAY = [];
export default connect((state, props) => {
  const {
    relationshipPaths,
    delegate
  } = props;
  const groupDelegate = actionCache.calc(() => {
    return DeviceUtil.getGroupDelegate(relationshipPaths, delegate, {
      queryName: 'deviceSelector'
    });
  }, relationshipPaths, delegate);
  const result = groupDelegate?.getList(state, {
    queryName: 'deviceSelector'
  });
  return {
    groupDelegate,
    values: result?.items || EMPTY_ARRAY,
    result
  };
})(DeviceSelectorValidator);